import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { CheckCircle2, CircleDashed, Rocket } from 'lucide-react';
import { WidgetContainer } from './WidgetContainer.js';
import type { DashboardOnboardingState } from '../../features/dashboard/api/dashboard.client.js';

export interface OnboardingChecklistWidgetProps {
  state: DashboardOnboardingState;
  onDismissHook: () => void;
  className?: string;
  testId?: string;
  loading?: boolean;
}

export function OnboardingChecklistWidget({
  state,
  onDismissHook,
  className,
  testId,
  loading = false,
}: OnboardingChecklistWidgetProps): ReactElement | null {
  const { t } = useTranslation();

  if (state.dismissed) {
    return null;
  }

  const steps = [
    { id: 'first_deal_created', label: t('crm.dashboard.onboarding.first_deal') },
    { id: 'client_contacted', label: t('crm.dashboard.onboarding.client_contacted') },
    { id: 'deal_won', label: t('crm.dashboard.onboarding.deal_won') },
  ];

  const totalSteps = steps.length;
  const completedStepsCount = steps.filter((s) => state.completedSteps.includes(s.id)).length;
  const progressPercent = Math.round((completedStepsCount / totalSteps) * 100);

  return (
    <WidgetContainer
      title={t('crm.dashboard.onboarding.title')}
      icon={Rocket}
      iconColor="#d97706"
      noPadding
      {...(className ? { className } : {})}
      {...(testId ? { testId } : {})}
      headerRight={
        <button
          type="button"
          onClick={onDismissHook}
          style={{
            fontSize: 12,
            color: 'var(--ant-color-text-tertiary)',
            fontWeight: 500,
            transition: 'color 0.2s',
            cursor: 'pointer',
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = 'var(--ant-color-text)')}
          onMouseOut={(e) => (e.currentTarget.style.color = 'var(--ant-color-text-tertiary)')}
        >
          {t('crm.dashboard.onboarding.dismiss')}
        </button>
      }
    >
      {loading ? (
        <div className="animate-pulse p-5 space-y-4">
          <div className="h-4 bg-gray-100 rounded w-full" />
          <div className="h-4 bg-gray-100 rounded w-3/4" />
        </div>
      ) : (
        <div className="flex flex-col md:flex-row p-6 gap-8">
          <div className="flex-1 flex flex-col justify-center">
            <h3
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: 'var(--color-text)',
                marginBottom: 4,
                letterSpacing: '-0.02em',
              }}
            >
              {t('crm.dashboard.onboarding.welcome_title')}
            </h3>
            <p style={{ fontSize: 14, color: 'var(--ant-color-text-secondary)', marginBottom: 20 }}>
              {t('crm.dashboard.onboarding.welcome_hint')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--ant-color-text)',
                }}
              >
                <span>{t('crm.dashboard.onboarding.progress_label')}</span>
                <span>{progressPercent}%</span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: 6,
                  background: 'var(--g-fill)',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    background: 'var(--ant-color-primary)',
                    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex-[1.5] border border-[var(--ant-color-border-secondary)] rounded-md bg-[var(--color-surface)] p-5">
            <ul
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}
            >
              {steps.map((step) => {
                const isCompleted = state.completedSteps.includes(step.id);
                const Icon = isCompleted ? CheckCircle2 : CircleDashed;
                const color = isCompleted
                  ? 'var(--ant-color-success)'
                  : 'var(--ant-color-text-quaternary)';

                return (
                  <li
                    key={step.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      opacity: isCompleted ? 0.7 : 1,
                    }}
                  >
                    <div
                      style={{
                        padding: 4,
                        borderRadius: '50%',
                        background: isCompleted
                          ? 'var(--ant-color-success-bg-hover)'
                          : 'var(--ant-color-bg-container)',
                        border: isCompleted
                          ? '1px solid var(--ant-color-success-border)'
                          : '1px solid var(--ant-color-border)',
                      }}
                    >
                      <Icon size={18} color={color} />
                    </div>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: isCompleted
                          ? 'var(--ant-color-text-secondary)'
                          : 'var(--color-text)',
                        textDecoration: isCompleted ? 'line-through' : 'none',
                      }}
                    >
                      {step.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </WidgetContainer>
  );
}
